import { useCallback, useEffect, useMemo, useState } from "react";
import { DadoConsentimento } from "@/app/services/crefisaService/getConsent";
import { consentToBankAccount } from "@/app/services/crefisaService/consentToBankAccount";
import { IBankAccount } from "@/app/services/crefisaService/createClient";

interface UseConsentSelectionProps {
  /** dados de consentimento devolvidos pela simulação */
  simulationConsents?: DadoConsentimento[];
  setCustomer: (data: {
    dataConsentId?: string;
    consentimentoId?: string;
    bankAccount?: IBankAccount;
  }) => void;
  setConsentBankAccount: (account: IBankAccount | null) => void;
}

const hasAccount = (
  item?: DadoConsentimento | null,
): item is DadoConsentimento => !!item && !!item.conta;

/**
 * Resolve qual consentimento (dados bancários) será usado no cadastro.
 * - 1 opção: usada automaticamente;
 * - 2 ou mais: o usuário escolhe (o avanço fica bloqueado até a escolha);
 * - "Verificar consentimento" substitui a lista vinda da simulação.
 */
export function useConsentSelection({
  simulationConsents,
  setCustomer,
  setConsentBankAccount,
}: UseConsentSelectionProps) {
  const [verifiedConsents, setVerifiedConsents] = useState<
    DadoConsentimento[] | null
  >(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const options = useMemo(
    () => (verifiedConsents ?? simulationConsents ?? []).filter(hasAccount),
    [verifiedConsents, simulationConsents],
  );

  const selected = useMemo(() => {
    if (options.length === 1) return options[0];
    return options.find((o) => o.dadosConsentimentoId === selectedId) ?? null;
  }, [options, selectedId]);

  // descarta escolha que deixou de existir na lista atual
  useEffect(() => {
    if (
      selectedId &&
      !options.some((o) => o.dadosConsentimentoId === selectedId)
    ) {
      setSelectedId(null);
    }
  }, [options, selectedId]);

  useEffect(() => {
    if (!selected) return;

    const bankAccount = consentToBankAccount(selected);
    setConsentBankAccount(bankAccount);
    setCustomer({
      ...(selected.dadosConsentimentoId
        ? { dataConsentId: selected.dadosConsentimentoId }
        : {}),
      ...(selected.consentimentoId
        ? { consentimentoId: selected.consentimentoId }
        : {}),
      bankAccount,
    });
  }, [selected, setCustomer, setConsentBankAccount]);

  const applyVerifiedConsents = useCallback((items: DadoConsentimento[]) => {
    setVerifiedConsents(items);
    setSelectedId(null);
  }, []);

  return {
    consentOptions: options,
    selectedConsent: selected,
    selectedConsentId: selected?.dadosConsentimentoId ?? null,
    selectConsent: setSelectedId,
    applyVerifiedConsents,
    /** há mais de um consentimento e nenhum foi escolhido */
    consentChoicePending: options.length > 1 && !selected,
  };
}
